import React, { Fragment, Suspense, useRef } from 'react';
import {
  Box,
  List,
} from '@mui/material';

import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useInView } from '@shared/hooks/useInView';

import { useInfiniteSpecializations } from '../hooks/useInfiniteSpecializations';

interface ViewProps {
  filters?: Specializations.GetListParams;
  renderItem: (job: Specializations.Specialization) => React.ReactNode;
  renderSkeleton: () => React.ReactNode;
}

export const InfiniteSpecializationsList: React.FC<ViewProps> = ({ 
  filters = {}, 
  renderItem, 
  renderSkeleton, 
}) => {
  const { specializations, status, fetchNextPage, hasNextPage, isFetching } = useInfiniteSpecializations(filters);

  const loadNextRef = useRef<HTMLElement>(undefined);
  const isInView = useInView(loadNextRef);

  const _renderSkeleton = React.useCallback(() => {
    if (isFetching) {
      return Array.from({ length: 3 }).map((v, idx) => <Fragment key={idx}>{renderSkeleton()}</Fragment>);
    }
  }, [isFetching, renderSkeleton]);

  React.useEffect(() => {
    if (isInView && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isInView, isFetching, fetchNextPage, hasNextPage]);

  return (
    <React.Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%" />
      )}
      {status === 'success' && specializations.length === 0 ? (
        <MessageFeedbackView 
          message="No specializations"
          height="100%"
        />
      ) : (
        <List disablePadding>
          {specializations.map(specialization => (
            <Suspense key={specialization.id} fallback={<>{renderSkeleton()}</>}>
              {renderItem(specialization)}
            </Suspense>
          ))}
        </List>
      )}

      <Box height="1px" ref={loadNextRef} />
      {hasNextPage && _renderSkeleton()}
    </React.Fragment>
  );
};
